<template>
  <v-card class="layout modern-table" elevation="0">
    <TempStarsTableTitle
      v-if="shouldShowTitle"
      :icon="tableIcon"
      :title="formattedTableName"
      :count="tableContent.length"
    />
    <PageExplanation :page-key="tablename" />

    <div class="tempstars-table-header">
      <div class="header-content">
        <div class="header-actions">
          <v-text-field
            v-model.lazy="table_search"
            class="search-field"
            prepend-inner-icon="mdi-magnify"
            label="Search"
            outlined
            dense
            hide-details
          ></v-text-field>

          <!-- New Item Button for Blocked and Favourite tables -->
          <v-btn
            v-if="
              tablename === 'BlockedHygienist' ||
                tablename === 'BlockedDentist' ||
                tablename === 'FavouriteHygienist' ||
                tablename === 'FavouriteDentist'
            "
            color="#0B4598"
            dark
            elevation="0"
            class="new-item-btn"
            @click="dialog = true"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Item
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Add Blocked/Favourite Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card class="dialog-card">
        <v-card-title class="dialog-title tempstars-card-title">
          <v-icon color="white" class="mr-2">
            {{
              tablename.includes("Favourite") ? "mdi-star" : "mdi-account-lock"
            }}
          </v-icon>
          {{
            tablename.includes("Favourite")
              ? "Add Favourite Entry"
              : "Add Blocked Entry"
          }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-6">
          <v-text-field
            v-model="hygienistId"
            label="Hygienist ID"
            outlined
            dense
            type="number"
            prepend-inner-icon="mdi-account"
            placeholder="Enter hygienist ID"
          ></v-text-field>

          <v-text-field
            v-model="dentistId"
            :label="
              tablename === 'BlockedDentist'
                ? 'Dentist ID (Optional)'
                : 'Dentist ID'
            "
            outlined
            dense
            type="number"
            prepend-inner-icon="mdi-office-building"
            placeholder="Enter dentist ID"
            class="mt-4"
          ></v-text-field>

          <v-text-field
            v-if="
              tablename === 'FavouriteHygienist' ||
                tablename === 'BlockedHygienist' ||
                tablename === 'BlockedDentist'
            "
            v-model="locationId"
            :label="
              tablename === 'BlockedHygienist' ||
              tablename === 'BlockedDentist'
                ? 'Location ID'
                : 'Location ID (Optional)'
            "
            outlined
            dense
            type="number"
            prepend-inner-icon="mdi-map-marker"
            placeholder="Enter location ID"
            :required="
              tablename === 'BlockedHygienist' ||
                tablename === 'BlockedDentist'
            "
            class="mt-4"
          ></v-text-field>

          <v-alert dense outlined type="info" class="mt-4">
            <div class="text-caption">
              {{
                tablename.includes("Favourite")
                  ? "This will mark the hygienist as a favourite for this dental office."
                  : "This will block the hygienist from viewing shifts at this dental office."
              }}
            </div>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text color="#2C3E50" @click="closeDialog">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#0B4598"
            dark
            elevation="0"
            @click="addRow(hygienistId, dentistId)"
            :disabled="
              !hygienistId ||
                (tablename !== 'BlockedDentist' && !dentistId) ||
                ((tablename === 'BlockedHygienist' ||
                  tablename === 'BlockedDentist') &&
                  !locationId)
            "
          >
            <v-icon small left>mdi-check</v-icon>
            {{
              tablename.includes("Favourite")
                ? "Add Favourite Entry"
                : "Add Blocked Entry"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Password Reset Dialog -->
    <PasswordResetDialog
      ref="passwordResetDialog"
      @password-reset-success="handlePasswordResetSuccess"
    />

    <!-- Unsubscribe Dialog -->
    <UnsubscribeDialog
      ref="unsubscribeDialog"
      @unsubscribe-success="handleUnsubscribeSuccess"
    />

    <!-- Data Table -->
    <v-data-table
      calculate-widths
      :search="table_search"
      :headers="computedHeaders"
      :items="formattedContent"
      class="modern-data-table"
    >
      <template v-slot:no-data>
        <div class="text-center py-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4"
            >mdi-database-remove</v-icon
          >
          <div class="text-h6 grey--text text--darken-1">No records found</div>
        </div>
      </template>
      <template class="layout new" v-slot:body="{ items, headers }">
        <tbody class="new" name="list" is="transition-group" mode="out-in">
          <tr v-for="(item, idx) in items" :key="idx">
            <template v-for="(header, key) in headers">
              <td
                v-if="header.value !== 'actions'"
                :key="key"
                :class="[
                  'smaller',
                  { 'user-cell-centered': tablename === 'User' }
                ]"
              >
                <EditDate
                  v-if="
                    header.value === 'postedStart' ||
                      header.value === 'postedEnd'
                  "
                  :columnName="header.value"
                  :item="item[header.value]"
                  :itemID="item.id"
                />
                <div
                  v-else-if="
                    tablename === 'User' && header.value === 'status'
                  "
                  class="user-account-status"
                >
                  <v-chip
                    small
                    dark
                    :color="getUserStatusColor(item.status)"
                  >
                    {{ getUserStatusLabel(item.status) }}
                  </v-chip>
                </div>
                <div
                  v-else-if="
                    tablename === 'User' && header.value === 'accountStatus'
                  "
                  class="user-account-status"
                >
                  <v-chip
                    small
                    dark
                    :color="getAccountStatusColor(item.accountStatus)"
                  >
                    {{ getAccountStatusLabel(item.accountStatus) }}
                  </v-chip>
                </div>
                <UpdateMultipleItems
                  v-else
                  :item="item"
                  :column="header.value"
                  :disabled="blocked(header.value)"
                  :display-value="knownEnumDisplay(header.value, item[header.value])"
                />
              </td>
              <td
                v-else-if="tablename === 'User'"
                :key="`action-${key}`"
                class="actions-column user-actions-column"
              >
                <div class="user-action-buttons">
                  <v-btn
                    @click="openPasswordResetDialog(item)"
                    small
                    color="#0B4598"
                    dark
                    depressed
                    class="action-btn-primary"
                  >
                    <v-icon left small>mdi-lock-reset</v-icon>
                    Reset Password
                  </v-btn>
                  <v-btn
                    @click="openUnsubscribeDialog(item)"
                    small
                    color="#FB7672"
                    dark
                    depressed
                    class="action-btn-secondary"
                  >
                    <v-icon left small>mdi-account-remove</v-icon>
                    Unsubscribe
                  </v-btn>
                </div>
              </td>
              <td v-else :key="`action-${key}`" class="actions-column">
                <v-btn
                  @click="deleteRow(item.id)"
                  color="#FB7672"
                  dark
                  small
                  depressed
                  class="delete-btn"
                >
                  <v-icon left small>mdi-delete</v-icon>
                  Remove
                </v-btn>
              </td>
            </template>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import { constants } from "@/utils/constants";
import UpdateMultipleItems from "./components/UpdateMultipleItems";
import EditDate from "./components/EditDate";
import PasswordResetDialog from "@/components/Dialogs/PasswordResetDialog";
import UnsubscribeDialog from "@/components/Dialogs/UnsubscribeDialog";
import {
  addBlocked,
  removeBlocked,
  addFavourite,
  removeFavourite
} from "@/api";
import { convertValues } from "@/helpers";
import {
  formatKnownTableEnum,
  formatUserAccountStatus
} from "@/helpers/adminEnumLabels";
import { startCase } from "lodash";

export default Vue.extend({
  components: {
    UpdateMultipleItems,
    EditDate,
    PasswordResetDialog,
    UnsubscribeDialog,
    TempStarsTableTitle
  },
  data() {
    return {
      table_search: "",
      dialog: false,
      hygienistId: "",
      dentistId: "",
      locationId: ""
    };
  },
  mounted() {
    this.$store.commit("modify", {
      property: "tableHeaders",
      value: this.tableHeaders
    });
  },
  computed: {
    ...mapState(["tableHeaders", "tableContent", "tablename"]),
    formattedTableName() {
      return this.tablename.replace(/([A-Z]+)*([A-Z][a-z])/g, "$1 $2");
    },
    tableIcon() {
      const iconMap = {
        User: "mdi-account-circle",
        BlockedHygienist: "mdi-account-cancel",
        FavouriteHygienist: "mdi-star",
        BlockedDentist: "mdi-account-cancel",
        FavouriteDentist: "mdi-star"
      };
      return iconMap[this.tablename] || "mdi-table";
    },
    shouldShowTitle() {
      const titledTables = [
        "User",
        "BlockedHygienist",
        "FavouriteHygienist",
        "BlockedDentist",
        "FavouriteDentist"
      ];
      return titledTables.includes(this.tablename);
    },
    formattedContent() {
      const content = convertValues(this.tableContent);
      if (this.tablename === "User") {
        return content.map(item => {
          const formattedItem = { ...item };
          Object.keys(formattedItem).forEach(key => {
            const value = formattedItem[key];
            if (key === "accountStatus") {
              return;
            }
            if (
              value === null ||
              value === undefined ||
              value === "" ||
              (typeof value === "string" && value.trim() === "")
            ) {
              formattedItem[key] = "-";
            }
          });
          return formattedItem;
        });
      }
      return content;
    },
    computedHeaders() {
      const headers = [...this.tableHeaders];
      if (this.tablename === "User") {
        headers.forEach(header => {
          if (header.value !== "actions") {
            header.align = "center";
            header.text = this.formatUserHeaderText(
              header.text || header.value
            );
          }
        });
      }
      if (
        this.tablename === "BlockedHygienist" ||
        this.tablename === "BlockedDentist" ||
        this.tablename === "FavouriteHygienist" ||
        this.tablename === "FavouriteDentist" ||
        this.tablename === "User"
      ) {
        headers.push({
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          width: this.tablename === "User" ? "220px" : "120px"
        });
      }
      return headers;
    }
  },
  methods: {
    ...mapMutations(["modify", "clean"]),
    knownEnumDisplay(column, value) {
      return formatKnownTableEnum(this.tablename, column, value);
    },
    updateCountDays(value) {
      this.modify({ property: "countDays", value: value });
    },
    blocked(header) {
      return (
        constants.Blocked.tables.includes(this.tablename) ||
        (constants.Blocked.columns[this.tablename] &&
          (constants.Blocked.columns[this.tablename].includes(header) ||
            constants.Blocked.columns[this.tablename] === "all"))
      );
    },
    isAccountDeleted(status) {
      if (status === null || typeof status === "undefined") {
        return false;
      }
      const deletedCode =
        constants.USER_STATUSES && constants.USER_STATUSES.DELETED
          ? constants.USER_STATUSES.DELETED
          : -4;
      return Number(status) === deletedCode;
    },
    formatUserHeaderText(label) {
      if (!label) {
        return "";
      }
      const normalizedSource = label
        .toString()
        .replace(/_/g, " ")
        .replace(/id$/i, "Id")
        .replace(/amount$/i, "Amount")
        .replace(/status$/i, "Status");
      const base = startCase(
        normalizedSource.replace(/([a-z])([A-Z])/g, "$1 $2")
      );
      return base.replace(/\bId\b/g, "ID").replace(/\bUrl\b/g, "URL");
    },
    getUserStatusLabel(status) {
      return formatUserAccountStatus(status);
    },
    getUserStatusColor(status) {
      return this.isAccountDeleted(status) ? "#FB7672" : "#13CA82";
    },
    getAccountStatusLabel(status) {
      return formatUserAccountStatus(status);
    },
    getAccountStatusColor(status) {
      return this.isAccountDeleted(status) ? "#FB7672" : "#13CA82";
    },
    closeDialog() {
      this.dialog = false;
      this.hygienistId = "";
      this.dentistId = "";
      this.locationId = "";
    },
    async deleteRow(id) {
      try {
        if (
          this.tablename === "FavouriteHygienist" ||
          this.tablename === "FavouriteDentist"
        ) {
          await removeFavourite(this.tablename, id);
          this.modify({
            property: "snackbar",
            value: {
              visible: true,
              text: "Favourite entry removed successfully",
              color: "green darken-2"
            }
          });
        } else {
          await removeBlocked(this.tablename, id);
          this.modify({
            property: "snackbar",
            value: {
              visible: true,
              text: "Blocked entry removed successfully",
              color: "green darken-2"
            }
          });
        }
        await this.$store.dispatch("getData", { tablename: this.tablename });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove entry";
        this.modify({
          property: "snackbar",
          value: {
            visible: true,
            text: errorMessage,
            color: "red darken-2"
          }
        });
      }
    },
    async addRow(hygienistId, dentistId) {
      if (!hygienistId || (!dentistId && this.tablename !== "BlockedDentist")) {
        return;
      }
      if (
        (this.tablename === "BlockedHygienist" ||
          this.tablename === "BlockedDentist") &&
        !this.locationId
      ) {
        return;
      }
      try {
        this.dialog = false;
        const data = {
          hygienistId: Number(hygienistId)
        };
        if (dentistId) {
          data.dentistId = Number(dentistId);
        }
        if (
          this.tablename === "BlockedHygienist" ||
          this.tablename === "BlockedDentist"
        ) {
          data.locationId = Number(this.locationId);
        } else if (this.tablename === "FavouriteHygienist" && this.locationId) {
          data.locationId = Number(this.locationId);
        }
        if (
          this.tablename === "FavouriteHygienist" ||
          this.tablename === "FavouriteDentist"
        ) {
          await addFavourite(data, this.tablename);
          this.modify({
            property: "snackbar",
            value: {
              visible: true,
              text: "Favourite entry added successfully",
              color: "green darken-2"
            }
          });
        } else {
          await addBlocked(data, this.tablename);
          this.modify({
            property: "snackbar",
            value: {
              visible: true,
              text: "Blocked entry added successfully",
              color: "green darken-2"
            }
          });
        }
        this.hygienistId = this.dentistId = this.locationId = "";
        await this.$store.dispatch("getData", { tablename: this.tablename });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to add entry";
        this.modify({
          property: "snackbar",
          value: {
            visible: true,
            text: errorMessage,
            color: "red darken-2"
          }
        });
        // Reopen dialog on error so user can try again
        this.dialog = true;
      }
    },
    openPasswordResetDialog(user) {
      console.log("🔐 Tables: Opening password reset dialog for user:", user);
      const passwordResetDialog = this.$refs.passwordResetDialog;
      if (
        passwordResetDialog &&
        typeof passwordResetDialog.openDialog === "function"
      ) {
        passwordResetDialog.openDialog({
          id: user.id,
          email: user.email,
          hygienistId: user.hygienistId,
          dentistId: user.dentistId
        });
      }
    },
    handlePasswordResetSuccess(data) {
      console.log("🔐 Tables: Password reset successful:", data);
      this.modify({
        property: "snackbar",
        value: {
          visible: true,
          text: `Password reset successful for user ${data.result.userEmail}`,
          color: "green darken-2"
        }
      });
    },
    openUnsubscribeDialog(user) {
      console.log("📧 Tables: Opening unsubscribe dialog for user:", user);
      const unsubscribeDialog = this.$refs.unsubscribeDialog;
      if (
        unsubscribeDialog &&
        typeof unsubscribeDialog.openDialog === "function"
      ) {
        unsubscribeDialog.openDialog({
          id: user.id,
          email: user.email,
          hygienistId: user.hygienistId,
          dentistId: user.dentistId
        });
      }
    },
    handleUnsubscribeSuccess(data) {
      console.log("📧 Tables: Unsubscribe successful:", data);
      this.modify({
        property: "snackbar",
        value: {
          visible: true,
          text: `User ${data.result.userEmail} unsubscribed from emails successfully`,
          color: "green darken-2"
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
@import "~@/styles/variables/colors.scss";
@import "~@/styles/components/tempstars-table.scss";

.modern-table {
  border-radius: 8px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  margin-top: 16px;
}

.header-content {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-field {
  max-width: 300px;

  ::v-deep .v-input__control {
    border-radius: 8px;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }
}

.new-item-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0 24px !important;
  height: 40px !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(11, 69, 152, 0.3);
  }
}

.dialog-card {
  border-radius: 12px;
  overflow: hidden;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  padding: 20px 24px;
}

::v-deep .modern-data-table {
  .v-data-table__wrapper {
    border-radius: 0;
  }

  thead tr th {
    background: rgba(11, 69, 152, 0.05) !important;
    color: $primary-blue !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    padding: 16px 12px !important;
    white-space: nowrap;
    border-bottom: 2px solid rgba(11, 69, 152, 0.1) !important;
  }

  tbody tr {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(11, 69, 152, 0.03) !important;
    }

    &:nth-child(even) {
      background-color: rgba(0, 0, 0, 0.02);
    }

    td {
      padding: 12px !important;
      font-size: 14px;
      color: $text-dark;
    }
  }

  .v-data-footer {
    border-top: 2px solid rgba(11, 69, 152, 0.1);
    padding: 16px;

    .v-data-footer__select {
      .v-select {
        margin: 0;
      }
    }
  }
}

.delete-icon {
  width: 50px;
  padding: 0 !important;
  text-align: center;
}

.user-cell-centered {
  text-align: center !important;

  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center !important;
  }

  ::v-deep .v-edit-dialog {
    display: inline-block;
    text-align: center;
    width: 100%;
  }

  ::v-deep .v-edit-dialog__trigger {
    display: block;
    text-align: center;
    width: 100%;
  }
}

.actions-column {
  width: 120px;
  padding: 12px !important;
  text-align: center;
}

.user-actions-column {
  width: 220px !important;
}

.delete-btn {
  min-width: 90px !important;
  height: 32px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease !important;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(251, 118, 114, 0.4);
  }

  .v-icon {
    font-size: 16px !important;
  }
}

.action-icon {
  width: 220px;
  padding: 0 !important;
  text-align: center;
}

.user-action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.action-btn {
  font-size: 11px !important;
  padding: 4px 8px !important;
  min-width: auto !important;
  height: 28px !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
}

.action-btn-primary,
.action-btn-secondary {
  min-width: 100px !important;
  height: 32px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease !important;
  text-align: center !important;
  justify-content: center !important;
  display: inline-flex !important;
  align-items: center !important;

  &:hover {
    transform: translateY(-1px);
  }

  .v-icon {
    font-size: 16px !important;
  }

  ::v-deep .v-btn__content {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100%;
    text-align: center;
  }
}

.action-btn-primary {
  &:hover {
    box-shadow: 0 3px 8px rgba(11, 69, 152, 0.4);
  }
}

.action-btn-secondary {
  &:hover {
    box-shadow: 0 3px 8px rgba(251, 118, 114, 0.4);
  }
}

.smaller {
  padding: 0 12px !important;
}

.layout {
  width: 100%;
}

.new {
  overflow: hidden;
  transition: height 0.3s ease-out;
  height: auto;
}

/* Legacy Styles */
.reviewButtons {
  width: 100px;
  display: flex;
  justify-content: space-between;
  padding: 40% 0;
}

.btnsBlock {
  display: flex;
  flex-direction: column;
}

.btnsBlock__btn {
  white-space: nowrap;
  padding: 6px 10px;
  font-weight: 700;
}

.circle {
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: inline-block;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .search-field {
    max-width: 100%;
    width: 100%;
  }

  .new-item-btn {
    width: 100%;
  }
}

.inputNumber {
  border: solid;
  border-radius: 0.4rem;
  width: 3rem;
  height: 2rem;
  margin-right: 0.8rem;
}

.round {
  border-radius: 10px;
}

.disabled {
  pointer-events: none;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  min-height: 70px;
}

.modal-container {
  width: 540px;
}

.v-card {
  display: block !important;
}
</style>
